import React, { useState, useEffect, useRef } from 'react';
import {
    Button, Popover, Typography, TextField, MenuItem, Box,
    Tooltip, IconButton, FormControlLabel, Switch
} from '@mui/material';
import FilterAltOutlinedIcon from '@mui/icons-material/FilterAltOutlined';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import FilterAlt from '@mui/icons-material/FilterAlt';
import moment from 'moment';
import { useTranslation } from 'react-i18next';

export const FilterTable = ({ colum, Search, ResetSearch, filter, filterValue }) => {
    const { t } = useTranslation();
    const [valueFiltStr, setValueFiltStr] = useState(filterValue?.value ?? '');
    const [filtStr, setFiltStr] = useState(filterValue?.operator ?? '=');
    const [filtSelNum, setFiltSelNum] = useState(filterValue?.operator ?? '=');
    const [filtNum, setFiltNum] = useState(filterValue?.value ?? 0);
    const [filtBool, setFiltBool] = useState(Boolean(filterValue?.value));
    const [filtDateInit, setFiltDateInit] = useState(
        filterValue?.initDate ? moment(filterValue.initDate, 'YYYY/MM/DD').toDate() : new Date()
    );
    const [filtDateEnd, setFiltDateEnd] = useState(
        filterValue?.endDate ? moment(filterValue.endDate, 'YYYY/MM/DD').toDate() : new Date()
    );
    const [anchorEl, setAnchorEl] = useState(null);
    const open = Boolean(anchorEl);

    const stringRef = useRef(null);
    const dateRef = useRef(null);
    const numberRef = useRef(null);
    const boolRef = useRef(null);

    const handleClick = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    useEffect(() => {
        if (open) {
            setTimeout(() => {
                if (colum.type === 'string' && stringRef.current) stringRef.current.focus();
                if (colum.type === 'date' && dateRef.current) dateRef.current.focus();
                if (colum.type === 'number' && numberRef.current) numberRef.current.focus();
                if (colum.type === 'boolean' && boolRef.current) boolRef.current.focus();
            }, 100);
        }
    }, [open, colum.type]);

    useEffect(() => {
        if (!filterValue) {
            setValueFiltStr('');
            setFiltStr('=');
            setFiltSelNum('=');
            setFiltNum(0);
            setFiltBool(false);
            setFiltDateInit(new Date());
            setFiltDateEnd(new Date());
            return;
        }

        if (colum.type === 'string') {
            setValueFiltStr(filterValue.value ?? '');
            setFiltStr(filterValue.operator ?? '=');
        }
        if (colum.type === 'number') {
            setFiltNum(filterValue.value ?? 0);
            setFiltSelNum(filterValue.operator ?? '=');
        }
        if (colum.type === 'boolean') {
            setFiltBool(Boolean(filterValue.value));
        }
        if (colum.type === 'date') {
            setFiltDateInit(filterValue.initDate ? moment(filterValue.initDate, 'YYYY/MM/DD').toDate() : new Date());
            setFiltDateEnd(filterValue.endDate ? moment(filterValue.endDate, 'YYYY/MM/DD').toDate() : new Date());
        }
    }, [filterValue, colum.type]);

    const handleKeyDown = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            SetFilter();
        }
    };

    const SetFilter = () => {
        let filt = {};
        if (colum.type === 'string') {
            filt = { value: valueFiltStr, operator: filtStr, type: colum.type };
        }
        if (colum.type === 'number' && filtNum > 0) {
            filt = { value: filtNum, operator: filtSelNum, type: colum.type };
        }
        if (colum.type === 'boolean') {
            filt = { value: filtBool, operator: '=', type: colum.type };
        }
        if (colum.type === 'date') {
            filt = {
                initDate: moment(filtDateInit).format('YYYY/MM/DD'),
                endDate: moment(filtDateEnd).format('YYYY/MM/DD'),
                operator: "BETWEEN",
                type: colum.type
            };
        }
        handleClose();
        Search(filt, colum.id);
    };

    const Clear = () => {
        if (colum.type === 'string') {
            setValueFiltStr('');
            setFiltStr('LIKE');
        }
        if (colum.type === 'number') {
            setFiltSelNum('=');
            setFiltNum(0);
        }
        if (colum.type === 'boolean') {
            setFiltBool(false);
        }
        if (colum.type === 'date') {
            setFiltDateInit(new Date());
            setFiltDateEnd(new Date());
        }
        handleClose();
        ResetSearch(colum.id);
    };

    return (
        <React.Fragment>
            <Popover
                id={`popoverId_${colum.id}`}
                open={open}
                anchorEl={anchorEl}
                onClose={handleClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                transformOrigin={{ vertical: 'top', horizontal: 'center' }}
                disableAutoFocus
                disableEnforceFocus
                className='custom-table-filter-zd-nx'
            >
                <Box
                    sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 2, maxWidth: 180 }}
                    onKeyDown={handleKeyDown}
                >
                    <Typography align='center' fontSize={13}>
                        {t("common.filter.recordsWithValue")}
                    </Typography>

                    {colum.type === 'string' && (
                        <>
                            <TextField
                                id={`SelectId_${colum.id}`}
                                select
                                label={t('common.filter.operator')}
                                variant="outlined"
                                value={filtStr}
                                onChange={(e) => setFiltStr(e.target.value)}
                                size="small"
                            >
                                <MenuItem value='LIKE'>{t("common.filter.contains")}</MenuItem>
                                <MenuItem value='='>{t("common.filter.equals")}</MenuItem>
                                <MenuItem value='<>'>{t("common.filter.notEquals")}</MenuItem>
                            </TextField>

                            <TextField
                                id={`Id_${colum.id}`}
                                inputRef={stringRef}
                                label={t('common.filter.value')}
                                variant="outlined"
                                size="small"
                                InputLabelProps={{ shrink: true }}
                                value={valueFiltStr}
                                onChange={(e) => setValueFiltStr(e.currentTarget.value)}
                            />
                        </>
                    )}

                    {colum.type === 'date' && (
                        <LocalizationProvider dateAdapter={AdapterDateFns}>
                            <DatePicker
                                id={`inittDateId_${colum.id}`}
                                inputRef={dateRef}
                                label="Init Date"
                                maxDate={new Date()}
                                value={filtDateInit}
                                onChange={(newValue) => setFiltDateInit(newValue)}
                                renderInput={(params) => (
                                    <TextField size="small" InputLabelProps={{ shrink: true }} {...params} />
                                )}
                                inputFormat="dd/MM/yyyy"
                            />
                            <DatePicker
                                id={`endDateId_${colum.id}`}
                                label="End Date"
                                maxDate={new Date()}
                                minDate={filtDateInit}
                                value={filtDateEnd}
                                onChange={(newValue) => setFiltDateEnd(newValue)}
                                renderInput={(params) => (
                                    <TextField size="small" InputLabelProps={{ shrink: true }} {...params} />
                                )}
                                inputFormat="dd/MM/yyyy"
                            />
                        </LocalizationProvider>
                    )}

                    {colum.type === 'number' && (
                        <>
                            <TextField
                                id={`SelNumId_${colum.id}`}
                                inputRef={numberRef}
                                select
                                label={t('common.filter.operator')}
                                variant="outlined"
                                value={filtSelNum}
                                onChange={(e) => setFiltSelNum(e.target.value)}
                                size="small"
                            >
                                <MenuItem value='='>{t("common.filter.equals")}</MenuItem>
                                <MenuItem value='>='>{t("common.filter.greaterOrEqual")}</MenuItem>
                                <MenuItem value='<='>{t("common.filter.lessOrEqual")}</MenuItem>
                                <MenuItem value='<>'>{t("common.filter.notEquals")}</MenuItem>
                            </TextField>

                            <TextField
                                id={`numberId_${colum.id}`}
                                label={t('common.filter.value')}
                                variant="outlined"
                                type='number'
                                size="small"
                                InputLabelProps={{ shrink: true }}
                                value={filtNum}
                                onChange={(e) => setFiltNum(e.currentTarget.value)}
                            />
                        </>
                    )}

                    {colum.type === 'boolean' && (
                        <FormControlLabel
                            label={t("selectOption")}
                            control={
                                <Switch
                                    id={`SwitchId_${colum.id}`}
                                    inputRef={boolRef}
                                    checked={filtBool}
                                    onChange={(e) => setFiltBool(e.target.checked)}
                                />
                            }
                        />
                    )}

                    <Button
                        className='custom-table-filter-button-zd-nx'
                        variant="contained"
                        color="primary"
                        size="small"
                        onClick={() => SetFilter()}
                    >
                        <IconButton
                            id={`id_`}
                            size="small"
                            sx={{ p: 0 }}
                        >
                            <FilterAltOutlinedIcon fontSize='15px' sx={{ fill: '#ffffffff' }} />
                        </IconButton>
                        {t("common.filter.filter")}
                    </Button>
                    <Button
                        className='custom-table-clean-button-zd-nx'
                        variant="contained"
                        autoFocus
                        size="small"
                        onClick={() => Clear()}
                    >
                        <IconButton
                            id={`id_`}
                            size="small"
                            sx={{ p: 0 }}
                        >
                            <DeleteOutlinedIcon fontSize='15px' sx={{ fill: '#000000ff' }} />
                        </IconButton>
                        {t("common.filter.clear")}
                    </Button>
                </Box>
            </Popover>

            <Tooltip title={t("common.filter.filter")}>
                <IconButton
                    id={`id_`}
                    size="small"
                    sx={{ p: 0 }}
                    onClick={handleClick}
                >
                    <FilterAlt fontSize='15px' className={filter && "custom-filter-active"} />
                </IconButton>
            </Tooltip>
        </React.Fragment>
    );
};
